import { useMemo } from 'react';
import { Box, Table, TableCell, TableHead, TableRow, Typography, Stack } from '@mui/material';
import { Card } from '@liminal/web-ui/primitives/Card';
import { styled } from '@mui/material/styles';
import { StatusPill } from '@liminal/web-ui/primitives/StatusPill';
import { DataTableBody } from '@liminal/web-ui/display/DataTableBody';
import { DetailRow } from '@liminal/web-ui/display/DetailRow';
import { useNudgeActivity } from '@/hooks/useNudges';
import { formatOrderDate } from '@liminal/utils/date';
import { CHANNEL_POLICY_LABELS, groupPlannedByStep, sequenceStepLabel } from '@/lib/nudges';
import { lifecycleOutcomeVariant } from '@/components/ui/statusHelpers';
import { UserIdLink } from '@/components/ui/UserIdLink';

// Every user's onboarding nudges in one place: what goes out in the next 24
// hours (by step, then by person) and what went out or was skipped in the
// last 24. The per-user view is the Nudges tab on a user's page.

const DASH = '—';

const SummaryGrid = styled(Box)(({ theme }) => ({
  display: 'grid',
  gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))',
  gap: theme.spacing(2),
}));

function when(value?: string): string {
  return value ? formatOrderDate(value) : DASH;
}

function channelPolicyLabel(policy?: string): string {
  return CHANNEL_POLICY_LABELS[policy ?? ''] ?? policy ?? DASH;
}

function UserCell({ userId, email }: { userId?: string; email?: string }) {
  return <UserIdLink userId={userId}>{email || undefined}</UserIdLink>;
}

export function NudgesPage() {
  const { data, isLoading, isError } = useNudgeActivity();
  const planned = useMemo(() => data?.planned ?? [], [data]);
  const sends = useMemo(() => data?.sends ?? [], [data]);
  const byStep = useMemo(() => groupPlannedByStep(planned), [planned]);

  const sent = sends.filter((s) => s.outcome === 'sent');
  const skipped = sends.filter((s) => s.outcome === 'skipped');
  const plannedPeople = new Set(planned.map((p) => p.userId)).size;
  const count = (n: number) => (isLoading || isError ? DASH : String(n));

  return (
    <Stack spacing={3}>
      <Card variant="elevated">
          <SummaryGrid>
            <DetailRow layout="stacked" label="Planned, next 24 hours" value={count(planned.length)} />
            <DetailRow layout="stacked" label="People" value={count(plannedPeople)} />
            <DetailRow
              layout="stacked"
              label="Sent, last 24 hours"
              value={data?.sendsTruncated ? `${count(sent.length)}+` : count(sent.length)}
            />
            <DetailRow layout="stacked" label="Skipped, last 24 hours" value={count(skipped.length)} />
          </SummaryGrid>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
            Times are estimates: the send limit per run, an opt-out or the user finishing a step can move or cancel
            a nudge. Users with a paused journey are not shown.
          </Typography>
      </Card>

      <Card variant="elevated" padding="none">
          <Typography variant="h6" sx={{ px: 2, pt: 2, pb: 1 }}>
            Next 24 hours, by step
          </Typography>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Sequence</TableCell>
                <TableCell>Message</TableCell>
                <TableCell>Channel</TableCell>
                <TableCell align="right">People</TableCell>
                <TableCell>First goes out</TableCell>
              </TableRow>
            </TableHead>
            <DataTableBody
              columns={5}
              isLoading={isLoading}
              isError={isError}
              isEmpty={byStep.length === 0}
              emptyMessage="Nothing planned for the next 24 hours."
              errorMessage="Could not load planned nudges."
            >
              {byStep.map((g) => (
                <TableRow key={g.key}>
                  <TableCell>{sequenceStepLabel(g.sequence, g.step)}</TableCell>
                  <TableCell>{g.title || DASH}</TableCell>
                  <TableCell>{channelPolicyLabel(g.channelPolicy)}</TableCell>
                  <TableCell align="right">{g.count}</TableCell>
                  <TableCell>{when(g.firstSendTime)}</TableCell>
                </TableRow>
              ))}
            </DataTableBody>
          </Table>
      </Card>

      <Card variant="elevated" padding="none">
          <Typography variant="h6" sx={{ px: 2, pt: 2, pb: 1 }}>
            Next 24 hours, by person
          </Typography>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Goes out</TableCell>
                <TableCell>User</TableCell>
                <TableCell>Sequence</TableCell>
                <TableCell>Channel</TableCell>
                <TableCell>Message</TableCell>
              </TableRow>
            </TableHead>
            <DataTableBody
              columns={5}
              isLoading={isLoading}
              isError={isError}
              isEmpty={planned.length === 0}
              emptyMessage="Nothing planned for the next 24 hours."
              errorMessage="Could not load planned nudges."
            >
              {planned.map((p) => (
                <TableRow key={`${p.userId}-${p.sequence}-${p.step}`}>
                  <TableCell>{when(p.sendTime)}</TableCell>
                  <TableCell>
                    <UserCell userId={p.userId} email={p.userEmail} />
                  </TableCell>
                  <TableCell>{sequenceStepLabel(p.sequence, p.step)}</TableCell>
                  <TableCell>{channelPolicyLabel(p.channelPolicy)}</TableCell>
                  <TableCell>{p.title || DASH}</TableCell>
                </TableRow>
              ))}
            </DataTableBody>
          </Table>
      </Card>

      <Card variant="elevated" padding="none">
          <Typography variant="h6" sx={{ px: 2, pt: 2, pb: 1 }}>
            Sent and skipped, last 24 hours
          </Typography>
          {data?.sendsTruncated && (
            <Typography variant="body2" color="text.secondary" sx={{ px: 2, pb: 1 }}>
              Showing the most recent 1,000.
            </Typography>
          )}
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>When</TableCell>
                <TableCell>User</TableCell>
                <TableCell>Sequence</TableCell>
                <TableCell>Outcome</TableCell>
                <TableCell>Channel</TableCell>
                <TableCell>Version</TableCell>
                <TableCell>Delivery</TableCell>
              </TableRow>
            </TableHead>
            <DataTableBody
              columns={7}
              isLoading={isLoading}
              isError={isError}
              isEmpty={sends.length === 0}
              emptyMessage="Nothing sent or skipped in the last 24 hours."
              errorMessage="Could not load sent nudges."
            >
              {sends.map((s) => (
                <TableRow key={`${s.userId}-${s.sequence}-${s.step}-${s.createTime}`}>
                  <TableCell>{when(s.createTime)}</TableCell>
                  <TableCell>
                    <UserCell userId={s.userId} email={s.userEmail} />
                  </TableCell>
                  <TableCell>{sequenceStepLabel(s.sequence, s.step)}</TableCell>
                  <TableCell>
                    <StatusPill
                      label={s.outcome === 'skipped' ? `skipped: ${s.skipReason ?? ''}` : s.outcome ?? DASH}
                      tone={lifecycleOutcomeVariant(s.outcome)}
                      size="sm"
                    />
                  </TableCell>
                  <TableCell>{s.channel || DASH}</TableCell>
                  <TableCell>{s.variant || DASH}</TableCell>
                  <TableCell>{s.deliveryStatus || DASH}</TableCell>
                </TableRow>
              ))}
            </DataTableBody>
          </Table>
      </Card>
    </Stack>
  );
}
